import { generateVideoObserverCallback, generateVideoObserverOptions } from '../ProductCardVideo/utils';
import { videoPlayerHandle } from '../PlayPauseButton/utils';
import playPauseButton from '../PlayPauseButton';
import { trackBrowseResultClick } from '../../utils/ConstructorTracking';
import { buildVideoElement } from '../ProductCardVideo';
import { imageSizes, buildSource } from '../../utils/productCardImages';
export class PromoTile {
  /**
   * @param {Object} builder - PromoTileBuilder instance
   */
  constructor(builder) {
    this.promoTileData = {
      imageUrl: builder.imageUrl || '',
      imageAlt: builder.imageAlt || '',
      videoUrl: builder.videoUrl || '',
      destinationUrl: builder.destinationUrl || '',
      size: builder.size || 1,
    };
    this.promoTileNode = builder.promoTileNode || undefined;
    this.promoTileMediaNode = undefined;
    this.promoTileMediaPodNode = undefined;
    this.extraBaseClassName = builder.baseClassName || '';

    this.#render();
  }

  /**
   * Render the promo tile
   */
  #render() {
    if (!this.promoTileNode) return;

    const mediaPodNode = document.createElement('div');
    mediaPodNode.classList.add('promo-tile-c-basis__media-wrapper');
    this.promoTileMediaPodNode = mediaPodNode;
    // If there's a destination URL, wrap content in a link
    const mediaNode = this.promoTileData.destinationUrl ? document.createElement('a') : document.createElement('div');
    this.promoTileMediaNode = mediaNode;

    if (mediaNode.tagName === 'A') {
      mediaNode.href = this.#normalizeDestinationUrl(this.promoTileData.destinationUrl);
      mediaNode.setAttribute('target', '_self');
    }

    if (this.promoTileData.videoUrl) {
      mediaNode.classList.add('promo-tile-c-basis__media--video');
      this.extraBaseClassName && mediaNode.classList.add(`${this.extraBaseClassName}__promo-tile-media-video`);
      mediaNode.appendChild(this.#buildVideo());
    } else if (this.promoTileData.imageUrl) {
      mediaNode.classList.add('promo-tile-c-basis__media--image');
      this.extraBaseClassName && mediaNode.classList.add(`${this.extraBaseClassName}__promo-tile-media-image`);
      mediaNode.appendChild(this.#buildImage());
    }

    this.promoTileMediaPodNode.appendChild(mediaNode);
    this.promoTileNode.appendChild(this.promoTileMediaPodNode);
    this.promoTileData.videoUrl && this.promoTileMediaPodNode.appendChild(this.#buildPlayPauseButton());

    if (mediaNode.tagName === 'A') {
      mediaNode.addEventListener('click', this.#clickTracking.bind(this), true);
    }

    setTimeout(() => {
      this.promoTileData.videoUrl && this.#setObserver();
      this.promoTileData.videoUrl && videoPlayerHandle(this.promoTileMediaPodNode);
    }, 0);
  }

  /**
   * Build a consistent source HTML element
   * @param {String} image - image url
   * @param {String} layout - type of layout either mobile, desktop or widescreen
   * @returns {HTMLSourceElement} - a source HTML element
   */
  #buildSource(image, layout) {
    return buildSource({ image, layout });
  }

  /**
   * Build an image element that fills the tile
   * @returns {HTMLImageElement}
   */
  #buildImage() {
    // Create promo tile picture element
    const productCardImage = document.createElement('picture');
    const img = document.createElement('img');
    img.classList.add('promo-tile-c-basis__image');
    img.src = this.promoTileData.imageUrl;
    img.alt = this.promoTileData.imageAlt;
    img.loading = 'lazy';
    img.setAttribute('width', 200);
    img.setAttribute('height', 300);

    for (const screenSize of Object.keys(imageSizes)) {
      productCardImage.appendChild(this.#buildSource(this.promoTileData.imageUrl, screenSize));
    }

    // Handle image load errors (no network request fallback)
    img.onerror = function () {
      this.parentNode.style.display = 'none';
      this.closest('a').style.background = '#fff';
    };

    productCardImage.appendChild(img);

    if (this.promoTileData.size !== 1) {
      return img;
    }
    return productCardImage;
  }

  /**
   * Build a video element that auto-plays muted and loops
   * @returns {HTMLVideoElement}
   */
  #buildVideo() {
    return buildVideoElement(this.promoTileData.videoUrl, this.promoTileData.imageAlt, {
      hidden: true,
      height: false,
      placement: 'promo_tile',
      fakePoster: false,
    });
  }

  /**
   * Observe video visibility and play/pause accordingly,
   * matching the product card lazy-load + play/pause behavior.
   * @param {HTMLElement} videoWrapper
   */
  #setObserver() {
    const videoObserverCallback = generateVideoObserverCallback(true);
    const videoObserverOptions = generateVideoObserverOptions(null);
    const video = this.promoTileNode.querySelector('.promo-tile-c-basis__media--video');
    if (video) {
      const observer = new IntersectionObserver(videoObserverCallback, videoObserverOptions);
      observer.observe(video);
    }
  }

  /**
   * Build the play/pause toggle button matching the PDP video-player pattern.
   * Uses the existing `#PromoTile__playPauseButton` style hook and the shared
   * `videoPlayerHandle` click logic (which toggles aria-label between
   * "Play video" / "Pause video").
   * @returns {HTMLButtonElement}
   */
  #buildPlayPauseButton() {
    return playPauseButton(this.extraBaseClassName);
  }

  #clickTracking() {
    if (!this.promoTileNode) return;

    const browseContainer = this.promoTileNode.closest('[data-cnstrc-browse]');
    if (!browseContainer) return;

    const resultId = this.promoTileNode.closest('[data-cnstrc-result-id]')?.dataset.cnstrcResultId || '';
    const itemId = this.promoTileNode.getAttribute('data-cnstrc-item-id');
    const itemName = this.promoTileNode.getAttribute('data-cnstrc-item-name');

    trackBrowseResultClick({
      filterName: 'group_id',
      filterValue: browseContainer.closest('[data-collection-id]')?.dataset.collectionId || '',
      itemId,
      itemName,
      resultId,
    });
  }

  /**
   * Normalize destination URL: when the URL points to the current store/origin,
   * return just the path so global link handlers (INF-links) don't treat it as
   * external and open it in a new tab.
   * @param {String} url
   * @returns {String}
   */
  #normalizeDestinationUrl(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin === window.location.origin) {
        return parsed.pathname + parsed.search + parsed.hash;
      }
      return parsed.href;
    } catch {
      return url;
    }
  }
}
